import { create } from 'zustand';
import { messageOf } from '@/api/miniapp/client';
import { answerValueForSave, normalizeAnswer, normalizeAnswers } from '@/api/miniapp/answers';
import { miniappApi } from '@/api/miniapp/miniappApi';
import { useAuthStore } from './authStore';
import type { AnswersMap, QuestionSchema, SaveAnswerPayload } from '@/api/miniapp/types';

/**
 * Анкета на реальном бэкенде.
 *
 * Схема вопросов приходит с сервера (`questions`), а не зашита в приложении:
 * состав анкеты определяет бэкенд и активный профиль. Ответы сохраняются по
 * одному (`answer`), завершение (`complete`) возвращает состояние сессии.
 */
interface BackendSurveyState {
  questions: QuestionSchema[];
  answers: AnswersMap;
  loading: boolean;
  saving: boolean;
  completing: boolean;
  error: string | null;

  /** Загрузить схему и текущие ответы активного профиля. */
  reload(): Promise<void>;
  /** false — сервер ответ не принял; локальное значение откатывается. */
  saveAnswer(payload: SaveAnswerPayload): Promise<boolean>;
  complete(): Promise<boolean>;
  reset(): void;
}

export const useBackendSurveyStore = create<BackendSurveyState>((set, get) => ({
  questions: [],
  answers: {},
  loading: false,
  saving: false,
  completing: false,
  error: null,

  async reload() {
    set({ loading: true, error: null });
    try {
      const [questions, answers] = await Promise.all([
        miniappApi.getQuestions(),
        miniappApi.getAnswers(),
      ]);
      const schema = questions ?? [];
      set({ questions: schema, answers: normalizeAnswers(schema, answers ?? {}), loading: false });
    } catch (error) {
      set({
        loading: false,
        error: messageOf(error, 'Не удалось загрузить анкету'),
      });
    }
  },

  async saveAnswer(payload) {
    // Локальный ответ применяется сразу: ждать сеть на каждом вопросе — тормоз.
    // Форма записи совпадает с ответом бэкенда, чтобы перезагрузка анкеты
    // не меняла структуру данных на экране
    const previous = get().answers[payload.key];
    const question = get().questions.find((item) => item.key === payload.key);
    const value = answerValueForSave(question, payload.value);

    set((state) => ({
      answers: {
        ...state.answers,
        // Локально хранится то же, что вернёт сервер после перезагрузки:
        // иначе ответ выглядел сохранённым, пока анкету не открывали заново
        [payload.key]: normalizeAnswer(question, {
          value,
          otherValue: payload.otherValue ?? null,
          comment: payload.comment ?? null,
        }),
      },
      saving: true,
      error: null,
    }));

    try {
      await miniappApi.saveAnswer({ ...payload, value });
      set({ saving: false });
      return true;
    } catch (error) {
      // Откат обязателен: иначе на экране ответ есть, на сервере его нет, и
      // при следующем открытии анкеты он молча исчезает
      set((state) => {
        const answers = { ...state.answers };
        if (previous) answers[payload.key] = previous;
        else delete answers[payload.key];

        return {
          answers,
          saving: false,
          error: messageOf(error, 'Ответ не сохранён'),
        };
      });
      return false;
    }
  },

  async complete() {
    set({ completing: true, error: null });
    try {
      const response = await miniappApi.completeSurvey();
      // Ответ переводит сессию в authenticated либо на следующий шаг
      useAuthStore.getState().applyResponse(response);
      set({ completing: false });
      return true;
    } catch (error) {
      set({
        completing: false,
        error: messageOf(error, 'Не удалось завершить анкету'),
      });
      return false;
    }
  },

  reset() {
    set({ questions: [], answers: {}, error: null });
  },
}));
